import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSnackBar } from "../../../../../../hooks/useSnackBar";
import {
  deleteCarrier,
  fetchHajexCarriers,
  updateAccountName,
  updateCarrierStatus,
  updateServiceStatus,
} from "../../../../../../services/ShipperServices/Carriers/carriers.services";
import { formatServiceLabel, isCarrierOn } from "../carrierHelpers";
import i18n from "../../../../../../i18n";

const PAGE_SIZE = 100;

const errorText = (error) => error?.response?.data?.message;

const patched = (id, patch) => (rows) =>
  rows.map((row) => (row.id === id ? { ...row, ...patch } : row));

// Requests in flight per row, so one finishing doesn't unlock a row another still holds.
const counted = (rows, delta) => (prev) => ({
  ...prev,
  ...Object.fromEntries(rows.map((row) => [row.id, (prev[row.id] ?? 0) + delta])),
});

// A single service change gets a toast like the status ones (develop showed one too).
const notifyService = (notify, subject, changes) => {
  const entries = Object.entries(changes);
  if (entries.length !== 1) return;
  const [[service, on]] = entries;
  const text = i18n.t(
    on ? "settings:carriers.serviceOn" : "settings:carriers.serviceOff",
    { service: formatServiceLabel(service), subject },
  );
  if (on) notify.success(text);
  else notify.info(text);
};

const useCarrierRows = (config) => {
  const notify = useSnackBar();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState({});
  // update-status flips the stored value whatever is_enable says, so a second
  // request for a row that is still in flight would undo the first one.
  const statusInFlight = useRef(new Set());
  // Bumped whenever a save gets its response, so a list loaded meanwhile is known to be stale.
  const savesDone = useRef(0);

  const busy = useMemo(
    () => Object.fromEntries(Object.entries(pending).map(([id, count]) => [id, count > 0])),
    [pending],
  );

  const reload = useCallback(async () => {
    let response;
    let before;
    do {
      before = savesDone.current;
      response = await fetchHajexCarriers(config.type, 1, PAGE_SIZE, "");
    } while (!response.error && savesDone.current !== before);
    if (response.error) notify.error(i18n.t("settings:carriers.loadFailed"));
    else setRows(response.data?.data?.data ?? []);
    setLoading(false);
  }, [config, notify]);

  useEffect(() => {
    reload();
  }, [reload]);

  const setEnabled = useCallback(
    async (targetRows, next) => {
      const targets = targetRows.filter(
        (row) => !statusInFlight.current.has(row.id) && isCarrierOn(row) !== next,
      );
      if (!targets.length) return;

      targets.forEach((row) => statusInFlight.current.add(row.id));
      setPending(counted(targets, 1));
      const errors = await Promise.all(
        targets.map(async (row) => {
          const { data, error } = await updateCarrierStatus({
            record_id: parseInt(row.id),
            is_enable: next,
          });
          if (!error) {
            savesDone.current += 1;
            setRows(
              patched(row.id, {
                is_enable: data?.result?.is_enable ?? { ...row.is_enable, user: next },
              }),
            );
          }
          statusInFlight.current.delete(row.id);
          setPending(counted([row], -1));
          return error;
        }),
      );

      const failed = errors.filter(Boolean);
      if (failed.length) {
        notify.error(errorText(failed[0]));
        return;
      }
      const subject =
        targets.length === 1
          ? config.subjectOf(targets[0])
          : i18n.t("settings:carriers.countCarriers", { count: targets.length });
      const text = config.enabledText(subject, next, targets.length);
      if (next) notify.success(text);
      else notify.info(text);
    },
    [config, notify],
  );

  const setServices = useCallback(
    async (targetRows, changesFor, successText) => {
      const targets = targetRows
        .map((row) => [row, changesFor(row)])
        .filter(([, changes]) => Object.keys(changes).length);
      if (!targets.length) return;

      setPending(counted(targets.map(([row]) => row), 1));
      const errors = await Promise.all(
        targets.map(async ([row, changes]) => {
          let services;
          let failure = null;
          for (const [key, value] of Object.entries(changes)) {
            const { data, error } = await updateServiceStatus({ [key]: value }, row.id);
            if (error) {
              failure = error;
              break;
            }
            services = data?.result?.services ?? services;
          }
          if (services) {
            savesDone.current += 1;
            setRows(patched(row.id, { services }));
          }
          setPending(counted([row], -1));
          return failure;
        }),
      );

      const failed = errors.filter(Boolean);
      if (failed.length) notify.error(errorText(failed[0]));
      else if (successText) notify.success(successText);
      else if (targets.length === 1) {
        const [[row, changes]] = targets;
        notifyService(notify, config.subjectOf(row), changes);
      }
    },
    [config, notify],
  );

  const rename = useCallback(
    async (row, accountName) => {
      // Same body, key order included, as develop's UpdateAccount.
      const { data, error } = await updateAccountName({
        account_name: accountName,
        record_id: row.id,
      });
      if (error) {
        notify.error(errorText(error));
        return false;
      }
      savesDone.current += 1;
      // Show the name as stored (the backend trims it).
      const saved = data?.result?.account_name;
      setRows(patched(row.id, { account_name: saved !== undefined ? saved : accountName }));
      notify.success(i18n.t("settings:carriers.accountUpdated"));
      return true;
    },
    [notify],
  );

  const remove = useCallback(
    async (row) => {
      const { error } = await deleteCarrier(row.id);
      if (error) {
        notify.error(i18n.t("settings:carriers.disconnectFailed"));
        return false;
      }
      savesDone.current += 1;
      setRows((prev) => prev.filter((item) => item.id !== row.id));
      notify.info(i18n.t("settings:carriers.disconnected"));
      return true;
    },
    [notify],
  );

  return { rows, loading, busy, setEnabled, setServices, rename, remove, reload };
};

export default useCarrierRows;
